/* eslint-disable max-classes-per-file */
import { NgIf } from '@angular/common';
import { Directive, EventEmitter, forwardRef, inject, InjectionToken, Input, Output } from '@angular/core';
import { RuntimeUtils } from 'webcommon/runtime-info';
import { foxitCssChunkName, FoxitModule, importFoxit, STATE_HANDLER_NAMES, ViewModes } from './FoxitUtil';

// these tokens are currently necessary to avoid circular references with webcommmon/shared
export const FoxitConfigServiceToken = new InjectionToken<FoxitConfigService>('FoxitConfigService');

export interface FoxitConfigService {
  getFoxitLicense(): Promise<FoxitConfig>;
}

export interface FoxitConfig {
  LicenseSN: string;
  LicenseKey: string;
  DefaultTool: STATE_HANDLER_NAMES;
  DefaultViewMode: ViewModes;
}

export const FoxitStyleServiceToken = new InjectionToken<FoxitStyleService>('FoxitStyleService');

export interface FoxitStyleService {
  addStyleUrlToDom(
    id: string,
    stylesheetUrl: Promise<string> | string,
  ): Promise<void>;
}




@Directive()
export class ApFoxitLoadedBaseDirective {
  readonly styleService = inject(FoxitStyleServiceToken);
  readonly runtimeUtils = inject(RuntimeUtils);
  readonly foxitConfigService = inject(FoxitConfigServiceToken);

  readonly foxitLoadedP: Promise<[FoxitModule, void, FoxitConfig]> = Promise.all([
    importFoxit(),
    this.styleService.addStyleUrlToDom(foxitCssChunkName, this.runtimeUtils.getFileUrlByChunk(foxitCssChunkName)),
    this.foxitConfigService.getFoxitLicense(),
  ]);
}

@Directive({
  selector: '[apUntilFoxitLoaded]',
  providers: [
    {
      provide: ApFoxitLoadedBaseDirective,
      useExisting: forwardRef(() => ApUntilFoxitLoadedDirective),
    },
  ],
  hostDirectives: [
    {
      directive: NgIf,
      inputs: [
        'ngIfElse: apUntilFoxitLoadedElse'
      ],
    },
  ],
  standalone: true,
})
export class ApUntilFoxitLoadedDirective extends ApFoxitLoadedBaseDirective {
  private ngIfDirective = inject<NgIf<boolean>>(NgIf);

  // This Input should be used to determine exactly what files to load via this.foxitLoadedP
  @Input('apUntilFoxitLoaded') loadParameters: 'default' = 'default';

  @Input() apUntilFoxitLoadedElse: ApUntilFoxitLoadedDirective['ngIfDirective']['ngIfElse'] = null;

  @Input('apUntilFoxitLoadedAfterLoad') afterLoad: (
    foxitModule: FoxitModule,
    foxitConfig: FoxitConfig,
  ) => void = () => {
    // by default, noop
  };

  constructor() {
    super();
    this.ngIfDirective.ngIf = false;
    this.foxitLoadedP.then(x => {
      this.afterLoad(x[0], x[2]);
      this.ngIfDirective.ngIf = true;
    });
  }
}

@Directive({
  selector: '[apFoxitLoaded]',
  providers: [
    {
      provide: ApFoxitLoadedBaseDirective,
      useExisting: forwardRef(() => ApFoxitLoadedDirective),
    },
  ],
  standalone: true,
})
export class ApFoxitLoadedDirective extends ApFoxitLoadedBaseDirective {
  @Output() apFoxitLoaded = new EventEmitter<{
    foxitModule: FoxitModule;
    foxitConfig: FoxitConfig;
  }>(true);

  constructor() {
    super();
    this.foxitLoadedP.then(x => {
      this.apFoxitLoaded.emit({
        foxitModule: x[0],
        foxitConfig: x[2],
      });
    });
  }
}
